/**
 * Promo Code apply/remove on the payment page. Mutually exclusive with DUO Code.
 */
class PromoCodeApply {
    constructor() {
        this.$input = document.getElementById('promo-code-input');
        this.$applyBtn = document.getElementById('promo-code-apply-btn');
        this.$removeBtn = document.getElementById('promo-code-remove-btn');
        this.$discountDisplay = document.getElementById('promo-code-discount-display');
        this.$errorDisplay = document.getElementById('promo-code-error');
        this.$overlay = document.getElementById('promo-code-loading-overlay');

        if (!this.$input || !this.$applyBtn) {
            return;
        }

        this.bindEvents();
        this.checkExistingPromoCode();
    }

    bindEvents() {
        this.$applyBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.applyCode();
        });

        this.$input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.applyCode();
            }
        });

        if (this.$removeBtn) {
            this.$removeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.removeCode();
            });
        }
    }

    async applyCode() {
        const code = this.$input.value.trim();
        if (!code) {
            this.showError('Please enter a promotion code.');
            return;
        }

        this.setLoading(true);
        this.clearError();

        const formData = new FormData();
        formData.append('action', 'wpcore_apply_promotion_code');
        formData.append('code', code);
        formData.append('nonce', wpcore.nonce);

        try {
            const response = await fetch(wpcore.ajaxUrl, {
                method: 'POST',
                body: formData,
            }).then((r) => r.json());

            if (!response.success) {
                this.showError(response.data?.message || 'Invalid promotion code.');
                this.setLoading(false);
                return;
            }

            this.onApplySuccess(response.data);
        } catch (error) {
            this.showError('An error occurred. Please try again.');
            console.error('Promo code apply error:', error);
        }

        this.setLoading(false);
    }

    async removeCode() {
        this.setLoading(true);

        const formData = new FormData();
        formData.append('action', 'wpcore_remove_promotion_code');
        formData.append('nonce', wpcore.nonce);

        try {
            const response = await fetch(wpcore.ajaxUrl, {
                method: 'POST',
                body: formData,
            }).then((r) => r.json());

            if (!response.success) {
                this.showError(response.data?.message || 'Failed to remove code.');
                this.setLoading(false);
                return;
            }

            this.onRemoveSuccess();
        } catch (error) {
            this.showError('An error occurred. Please try again.');
            console.error('Promo code remove error:', error);
        }

        this.setLoading(false);
    }

    onApplySuccess(data) {
        this.$input.closest('.promo-code-input-section')?.classList.add('hidden');
        if (this.$discountDisplay) {
            this.$discountDisplay.classList.remove('hidden');
            const labelEl = this.$discountDisplay.querySelector('.discount-label');
            if (labelEl) {
                labelEl.textContent = data.discount_label || 'Discount applied';
            }
            const codeEl = this.$discountDisplay.querySelector('.applied-code');
            if (codeEl) {
                codeEl.textContent = data.code || '';
            }
        }

        this.updatePriceDisplay(data);

        // Server already cleared DUO state; hide its UI to match.
        const duoDiscountDisplay = document.getElementById('duo-code-discount-display');
        if (duoDiscountDisplay) {
            duoDiscountDisplay.style.display = 'none';
        }
        const duoInput = document.getElementById('duo-code-input');
        if (duoInput) {
            duoInput.value = '';
        }

        this.clearError();
        this.$input.value = '';
    }

    onRemoveSuccess() {
        this.$input.closest('.promo-code-input-section')?.classList.remove('hidden');
        if (this.$discountDisplay) {
            this.$discountDisplay.classList.add('hidden');
        }
        this.resetPriceDisplay();
    }

    // Restore UI if the server has a previously applied code (page reload / new tab).
    checkExistingPromoCode() {
        if (window.wpcore_promo_code && window.wpcore_promo_code.applied) {
            this.onApplySuccess(window.wpcore_promo_code);
        }
    }

    formatEurPrice(amount) {
        return Number(amount).toFixed(2).replace('.', ',') + ' \u20AC';
    }

    updatePriceDisplay(data) {
        const priceEl = document.getElementById('subscription-price');
        const totalEl = document.getElementById('charged-today');
        const discountRowEl = document.getElementById('promo-code-discount-row');

        if (!priceEl || !totalEl) {
            return;
        }

        const originalPrice = parseFloat(priceEl.dataset.originalPrice || priceEl.textContent.replace(/[€$\s]/g, '').replace(',', '.'));
        let discountAmount = 0;

        if (data.discount_type === 'percent') {
            discountAmount = originalPrice * (parseFloat(data.discount_value) / 100);
        } else if (data.discount_type === 'fixed') {
            discountAmount = Math.min(parseFloat(data.discount_value), originalPrice);
        }

        const discountedPrice = Math.max(0, originalPrice - discountAmount);

        if (discountRowEl) {
            discountRowEl.classList.remove('hidden');
            const amountEl = discountRowEl.querySelector('.discount-amount');
            if (amountEl) {
                amountEl.textContent = '- ' + (data.discount_label || this.formatEurPrice(discountAmount));
            }
        }

        totalEl.textContent = this.formatEurPrice(discountedPrice);
    }

    resetPriceDisplay() {
        const priceEl = document.getElementById('subscription-price');
        const totalEl = document.getElementById('charged-today');
        const discountRowEl = document.getElementById('promo-code-discount-row');

        if (priceEl && totalEl) {
            totalEl.textContent = priceEl.textContent;
        }
        if (discountRowEl) {
            discountRowEl.classList.add('hidden');
        }
    }

    showError(message) {
        if (this.$errorDisplay) {
            this.$errorDisplay.textContent = message;
            this.$errorDisplay.classList.remove('hidden');
        }
    }

    clearError() {
        if (this.$errorDisplay) {
            this.$errorDisplay.textContent = '';
            this.$errorDisplay.classList.add('hidden');
        }
    }

    setLoading(loading) {
        if (this.$applyBtn) {
            this.$applyBtn.disabled = loading;
            this.$applyBtn.innerHTML = loading
                ? '<span class="animate-spin">...</span> Applying...'
                : wpcore.direct_t({ en: 'Apply', fr: 'Appliquer' });
        }
        if (this.$input) {
            this.$input.disabled = loading;
        }
        if (this.$overlay) {
            this.$overlay.classList.toggle('hidden', !loading);
        }
    }
}

export function initPromoCodeApply() {
    new PromoCodeApply();
}
